import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/components/shared";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, AlertTriangle, Clock } from "lucide-react";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "viewer";

  const load = () => {
    api.get("/notifications").then(({ data }) => setItems(data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const count = items.length;
  const overdue = items.filter((i) => i.status === "scaduta").length;

  const goto = (i) => {
    setOpen(false);
    if (isStaff) navigate(`/beni/${i.asset_id}`);
    else navigate("/dotazioni");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-secondary transition-colors">
          <Bell className="h-5 w-5 text-foreground" />
          {count > 0 && (
            <span data-testid="notification-badge"
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${overdue > 0 ? "bg-destructive" : "bg-primary"}`}>
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-heading font-bold text-base">Scadenze imminenti</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {count === 0 ? "Nessuna scadenza nei prossimi 30 giorni" : `${count} scadenza/e${overdue ? ` · ${overdue} scaduta/e` : ""}`}
          </div>
        </div>
        <ScrollArea className="max-h-80">
          {count === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Tutto in regola ✅</div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((i, idx) => (
                <button key={idx} onClick={() => goto(i)} data-testid={`notification-item-${idx}`}
                  className="w-full text-left px-4 py-3 hover:bg-secondary/60 transition-colors flex items-start gap-3">
                  <div className={`mt-0.5 shrink-0 ${i.status === "scaduta" ? "text-destructive" : "text-amber-500"}`}>
                    {i.status === "scaduta" ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{i.asset_name}</div>
                    <div className="text-xs text-muted-foreground">{i.activity_type_name} · {i.detail}</div>
                    <div className={`text-xs font-medium ${i.status === "scaduta" ? "text-destructive" : "text-amber-600"}`}>
                      {i.urgency}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
